import { useLayoutEffect, useState } from 'react'

const DEFAULT_SCHEDULE_HEIGHT = '100%'
const STACKED_STAFF_TITLE_OFFSET = 36

export const fitScheduleHeightToVisibleArea = ({
  rootClientHeight,
  visualHeight,
  rootTop,
  paddingY,
  headerHeight,
  extraOffset,
}: {
  rootClientHeight: number
  visualHeight?: number
  rootTop?: number
  paddingY: number
  headerHeight: number
  extraOffset: number
}): number => {
  const fromLayout = rootClientHeight - paddingY - headerHeight - extraOffset
  // 100vh includes Chrome's address bar, which makes the page scrollbar sit on the grid scrollbar.
  const fromVisual =
    visualHeight == null || rootTop == null
      ? fromLayout
      : visualHeight - rootTop - paddingY - headerHeight - extraOffset
  return Math.floor(Math.max(Math.min(fromLayout, fromVisual), 0))
}

const getScheduleHeight = (extraOffset = 0): string => {
  const scheduleHeader = document.getElementById('schedule-header-calendar')
  const root =
    (scheduleHeader?.closest('[data-schedule-root]') as HTMLElement | null) ??
    (scheduleHeader?.closest('.application-layout__content-container') as HTMLElement | null) ??
    document.querySelector<HTMLElement>('.application-layout__content-container')

  if (!root || !scheduleHeader) {
    return extraOffset ? `calc(100% - ${extraOffset}px)` : DEFAULT_SCHEDULE_HEIGHT
  }

  const style = getComputedStyle(root)
  const paddingY =
    (Number.parseFloat(style.paddingTop) || 0) + (Number.parseFloat(style.paddingBottom) || 0)
  const available = fitScheduleHeightToVisibleArea({
    rootClientHeight: root.clientHeight,
    visualHeight: window.visualViewport?.height,
    rootTop: root.getBoundingClientRect().top,
    paddingY,
    headerHeight: scheduleHeader.offsetHeight,
    extraOffset,
  })
  return `${available}px`
}

export const useScheduleHeight = (stacked = false) => {
  const extraOffset = stacked ? STACKED_STAFF_TITLE_OFFSET : 0
  const [scheduleHeight, setScheduleHeight] = useState(DEFAULT_SCHEDULE_HEIGHT)

  useLayoutEffect(() => {
    const updateHeight = () => setScheduleHeight(getScheduleHeight(extraOffset))
    updateHeight()

    const scheduleHeader = document.getElementById('schedule-header-calendar')
    const root =
      (scheduleHeader?.closest('[data-schedule-root]') as HTMLElement | null) ??
      (scheduleHeader?.closest('.application-layout__content-container') as HTMLElement | null) ??
      document.querySelector<HTMLElement>('.application-layout__content-container')

    window.addEventListener('resize', updateHeight)
    const viewport = window.visualViewport
    viewport?.addEventListener('resize', updateHeight)
    viewport?.addEventListener('scroll', updateHeight)
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateHeight)
    if (root) observer?.observe(root)
    if (scheduleHeader) observer?.observe(scheduleHeader)

    return () => {
      window.removeEventListener('resize', updateHeight)
      viewport?.removeEventListener('resize', updateHeight)
      viewport?.removeEventListener('scroll', updateHeight)
      observer?.disconnect()
    }
  }, [extraOffset])

  return scheduleHeight
}
